/**
 * bankReconcileAccess — geteilte Zugriffslogik für den Bank-Abgleich (BR).
 *
 * Single source of truth dafür, WER den Bank-Abgleich sehen und benutzen
 * darf — ab BR-3 genutzt von `BankReconciliationPage.tsx` (Inhalts-Gate).
 *
 * **Abweichung vom `RECEIPT_SCAN_BETA_UIDS`/`DATEV_EXPORT_BETA_UIDS`-Muster
 * (hartkodiertes Array in einem Config-Modul):** BR-0 hat für den
 * Bank-Abgleich bereits `App.tsx:94-102` mit einer ENV-VAR-getriebenen
 * Beta-Allowlist (`REACT_APP_BANK_RECONCILE_BETA_UIDS`, komma-separiert)
 * committet, NICHT mit einem hartkodierten Array. Code ist Source-of-Truth
 * für bestehende Interfaces (golden rules) — dieses Modul spiegelt daher
 * exakt dieselbe ENV-VAR-Logik, statt einen zweiten, abweichenden
 * Beta-UID-Mechanismus einzuführen. `App.tsx` bleibt unverändert (import
 * nicht nachgezogen, außerhalb des BR-3-Datei-Scopes) — beide Stellen lesen
 * denselben Env-Var, es gibt also keine divergente Werte-Quelle.
 *
 * Zwei Freischalt-Pfade (fail-closed — Default ist KEIN Zugriff):
 *   1. Beta-Allowlist: `REACT_APP_BANK_RECONCILE_BETA_UIDS` (komma-separierte
 *      UIDs) — kontrollierte Erprobung, unabhängig vom allgemeinen Flag.
 *   2. Allgemeine Freigabe: `REACT_APP_BANK_RECONCILE_ENABLED==='true'`.
 *
 * Tier-Term (architecture-pricing-repackaging.md §1/§3.2, ENTSCHIEDEN 2026-07-14):
 * die Gating-Entscheidung (O-1) ist GESCHLOSSEN — Bank-Abgleich ist ein
 * **Ultra**-Differenzierer. Bei allgemeiner Freigabe gilt `FLAG ∧ tier==='ultra'`
 * (wie `canUseReceiptScan`, nun ebenfalls Ultra). Inert, solange das Flag live
 * `false`/unset ist ⇒ nur die Beta-Allowlist greift (Business-Account 70Tgnu
 * bleibt via Beta-UID), byte-identisch zum IST.
 *
 * @module bankReconcileAccess
 */

import type { Tier } from './entitlements';

/**
 * Allgemeines Feature-Flag. Fail-closed: nur bei explizitem `'true'` aktiv.
 * Modul-Konstante, einmalig beim Bundle-Load ausgewertet.
 */
export const BANK_RECONCILE_FLAG: boolean =
  process.env.REACT_APP_BANK_RECONCILE_ENABLED === 'true';

/**
 * Beta-Erprobung Bank-Abgleich — ENV-VAR-getrieben (Muster `App.tsx`, s.
 * Modul-Kommentar), NICHT hartkodiert. Leer, solange die Env-Var nicht
 * gesetzt ist (fail-closed).
 */
export const BANK_RECONCILE_BETA_UIDS: readonly string[] = (
  process.env.REACT_APP_BANK_RECONCILE_BETA_UIDS ?? ''
)
  .split(',')
  .map((uid) => uid.trim())
  .filter((uid) => uid.length > 0);

/**
 * Entscheidet fail-closed, ob ein Nutzer den Bank-Abgleich sehen/benutzen darf.
 *
 * @param uid - Firebase-Auth-UID des aktuellen Nutzers (oder null/undefined,
 *              solange noch nicht eingeloggt).
 * @param tier - effektiver Tier des Nutzers (`resolveTier(sellerData)` aus
 *               `entitlements.ts`, §3.2). O-1 = Ultra (2026-07-14).
 * @returns `true`, wenn die UID auf der Beta-Allowlist steht ODER das
 *          allgemeine Flag aktiv ist UND der Nutzer den Ultra-Tier hat.
 *          Sonst `false`.
 */
export function canUseBankReconcile(uid: string | null | undefined, tier: Tier): boolean {
  if (uid && BANK_RECONCILE_BETA_UIDS.includes(uid)) {
    return true;
  }
  return BANK_RECONCILE_FLAG && tier === 'ultra';
}

// ---------------------------------------------------------------------------
// AVV-Consent-Gate (jurist-bank-haftung.md §5/§7.2, Auflage B) — fail-closed
// ---------------------------------------------------------------------------

/**
 * Aktuelle Bank-Abgleich-AVV-Version, deren Zustimmung erfasst und geprüft wird.
 * Zugleich der Dateiname-Stamm des AVV-Dokuments (Muster `INBOUND_AVV_VERSION`:
 * Version == Doc-Stem). Wird beim Consent server-persistent auf
 * `sellers/{uid}.bankAvvAcceptedVersion` geschrieben; das Import-Gate zeigt sich
 * erneut, sobald die gespeicherte Version von diesem Wert abweicht.
 *
 * Abweichung von der §7.2-Skizze (`'v1'`): hier bewusst der volle Dokument-Stamm
 * (kein reiner Rechtstext, sondern ein technischer Feldwert) — hält die
 * `BANK_AVV_DOC_PATH`-Ableitung konsistent zum Inbound-Muster und macht den
 * persistierten Consent-Record selbstsprechend.
 */
export const BANK_AVV_VERSION = 'avv-bank-reconciliation-v1';

/**
 * Öffentlicher Pfad des versionierten AVV-Dokuments (statisch ausgeliefert unter
 * `public/legal/`). Die Datei ist bereits vom Jurist freigegeben — der Coder
 * LIEST sie nur (per Laufzeit-Fetch), erstellt/ändert sie nie. Fehlt sie / liefert
 * die SPA index.html aus, greift der fail-closed Draft/Missing-Guard.
 */
export const BANK_AVV_DOC_PATH = `/legal/${BANK_AVV_VERSION}.md`;

/**
 * Marker, an dem der Entwurfs-Zustand des AVV-Dokuments erkannt wird (identisch
 * zum Inbound-/Advisor-Draft-Marker). Solange die Datei diesen Marker enthält,
 * bleibt der Import fail-closed gesperrt.
 */
export const BANK_AVV_DRAFT_MARKER = 'ENTWURF';

/**
 * Entscheidet fail-closed, ob für die aktuelle AVV-Version eine gültige
 * Zustimmung vorliegt. Einzige Wahrheitsquelle des Import-Gates — vergleicht die
 * server-persistierte `sellers/{uid}.bankAvvAcceptedVersion` exakt gegen
 * {@link BANK_AVV_VERSION}. Fehlt das Feld (Bestandsnutzer / noch nie zugestimmt)
 * oder trägt es eine ältere Version, ist der Import gesperrt.
 *
 * @param acceptedVersion Wert aus `sellerData.bankAvvAcceptedVersion` (oder
 *                        null/undefined, solange nicht zugestimmt wurde).
 * @returns `true` nur bei exakter Versions-Übereinstimmung.
 */
export function hasBankAvvConsent(
  acceptedVersion: string | null | undefined,
): boolean {
  return acceptedVersion === BANK_AVV_VERSION;
}
